import { createSelector } from '@reduxjs/toolkit';
import { DefaultRootState } from 'react-redux';
import { selectAll, selectById } from '../reducers/cell.reducer';
import { Cell, CellId } from '../types/cell.type';
import { computeSize, getCellCoordinates } from '../utils/strip';

export const cellStateSelector = ({ cell }: DefaultRootState) => cell;

export const cellsSelector = createSelector(cellStateSelector, selectAll);

export const cellSelector = (cellId: CellId) =>
  createSelector(cellStateSelector, (cellState) =>
    selectById(cellState, cellId),
  );

export const cellsByParentSelector = createSelector(cellsSelector, (cells) =>
  cells.reduce((byRow: { [cellId: string]: Cell[] }, cell) => {
    if (cell.type === 'root') {
      return byRow;
    }
    byRow[cell.parentId] = byRow[cell.parentId] ?? [];
    byRow[cell.parentId].push(cell);
    return byRow;
  }, {}),
);

export const positionSelector = (cellId: CellId) =>
  createSelector(
    cellStateSelector,
    cellsByParentSelector,
    (cellState, cellsByParent) =>
      getCellCoordinates(cellId, cellState, cellsByParent),
  );

export const sizeSelector = (cellId: CellId) =>
  createSelector(
    cellsByParentSelector,
    cellSelector(cellId),
    (cellsByParent) => {
      if (cellsByParent[cellId] == null) {
        return 0;
      }
      return computeSize(cellsByParent[cellId]);
    },
  );

export const subSizeSelector = (cellId: CellId) =>
  createSelector(
    cellsByParentSelector,
    cellSelector(cellId),
    (cellsByParent) => {
      if (cellsByParent[cellId] == null) {
        return 0;
      }

      const sizes = cellsByParent[cellId].map((child) => {
        if (cellsByParent[child.id] == null) {
          return 0;
        }
        return computeSize(cellsByParent[child.id]);
      });
      return Math.max(...sizes, 0);
    },
  );
